import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export type CouponType = "PERCENT" | "FIXED";
export type DynamicPromoStatus =
	| "active"
	| "inactive"
	| "expired"
	| "depleted"
	| "scheduled";

export interface Coupon {
	id: string;
	code: string;
	description: string | null;
	type: CouponType;
	value: number;
	minSubtotal: number;
	maxDiscount: number;
	isActive: boolean;
	startsAt: string | null;
	expiresAt: string | null;
	maxRedemptions: number | null;
	redeemedCount: number;
	productIds?: string[] | null;
	categoryIds?: string[] | null;
	oncePerCustomer?: boolean;
	allowCumulation?: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface CouponKpis {
	activeCount: number;
	scheduledCount: number;
	expiredCount: number;
	depletedCount: number;
	inactiveCount: number;
	totalCount: number;
	totalRedemptions30d: number;
	totalDiscountDistributed: number;
}

export interface PaginatedCoupons {
	items: Coupon[];
	total: number;
	page: number;
	limit: number;
	pages: number;
}

export interface PromotionsFilter {
	search?: string;
	status?: DynamicPromoStatus;
	type?: CouponType;
	page?: number;
	limit?: number;
}

export interface CreateCouponInput {
	code: string;
	description?: string | null;
	type: CouponType;
	value: number;
	minSubtotal?: number;
	maxDiscount?: number;
	isActive?: boolean;
	startsAt?: string | null;
	expiresAt?: string | null;
	maxRedemptions?: number | null;
	productIds?: string[] | null;
	categoryIds?: string[] | null;
	oncePerCustomer?: boolean;
	allowCumulation?: boolean;
}

export interface UpdateCouponInput {
	code?: string;
	description?: string | null;
	type?: CouponType;
	value?: number;
	minSubtotal?: number;
	maxDiscount?: number;
	isActive?: boolean;
	startsAt?: string | null;
	expiresAt?: string | null;
	maxRedemptions?: number | null;
	productIds?: string[] | null;
	categoryIds?: string[] | null;
	oncePerCustomer?: boolean;
	allowCumulation?: boolean;
}

const toQs = (filter: PromotionsFilter): string => {
	const usp = new URLSearchParams();
	if (filter.search?.trim()) usp.set("search", filter.search.trim());
	if (filter.status) usp.set("status", filter.status);
	if (filter.type) usp.set("type", filter.type);
	if (filter.page) usp.set("page", String(filter.page));
	if (filter.limit) usp.set("limit", String(filter.limit));
	const qs = usp.toString();
	return qs ? `?${qs}` : "";
};

export const promotionsApi = {
	list: (filter: PromotionsFilter = {}) =>
		unwrap<PaginatedCoupons>(
			api.get<ApiEnvelope<PaginatedCoupons>>(
				`/admin/promotions${toQs(filter)}`,
			),
		),

	getKpis: () =>
		unwrap<CouponKpis>(
			api.get<ApiEnvelope<CouponKpis>>("/admin/promotions/kpi"),
		),

	get: (id: string) =>
		unwrap<Coupon>(api.get<ApiEnvelope<Coupon>>(`/admin/promotions/${id}`)),

	create: (data: CreateCouponInput) =>
		unwrap<Coupon>(api.post<ApiEnvelope<Coupon>>("/admin/promotions", data)),

	update: (id: string, data: UpdateCouponInput) =>
		unwrap<Coupon>(
			api.patch<ApiEnvelope<Coupon>>(`/admin/promotions/${id}`, data),
		),

	toggleStatus: (id: string, isActive: boolean) =>
		unwrap<Coupon>(
			api.patch<ApiEnvelope<Coupon>>(`/admin/promotions/${id}/status`, {
				isActive,
			}),
		),
};
